import { MouseEvent as ReactMouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { KnmDataViolationPathsTemplateEnum } from 'APIPackage/KnmModule/enums/KnmDataViolationPathsTemplateEnum';
import { HEADER_STICKED_HEIGHT } from 'CorePackage/HeaderModule/constants/headerStickedHeight';
import { declOfNum } from 'CorePackage/MainModule/common/declOfNum';
import { getObjectBlockId, getObjectResultBlockId } from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import { IKnmDataViolationExtended } from 'KnmPackage/MainModule/interfaces/IKnmDataViolationExtended';
import { renderViolationValue } from 'KnmPackage/MainModule/common/renderViolationValue';

import { knmSectionsViolationsPathsTemplatesMap } from '../constants/knmSectionsViolationsPathsTemplatesMap';
import { menuViolationsDataByPathMap } from '../constants/menuViolationsDataByPathMap';
import { MenuItemsIdsEnum } from '../enums/MenuItemsIdsEnum';
import styles from './ViolationsErrorIcon.module.scss';

const countTitles = ['рабочий день', ' рабочих дня', 'рабочих дней'];

enum IViolationsGroupTypesEnum {
  DATA_FILLED = 'DATA_FILLED',
  DATA_EMPTY = 'DATA_EMPTY',
}

interface IViolationsGroupViolation extends IKnmDataViolationExtended {
  title: string;
  scrollElementId: string;
  reserveScrollElementId: string;
}

interface IViolationsGroup {
  count: IKnmDataViolationExtended['countDateViolation'];
  groupType: IViolationsGroupTypesEnum;
  violations: IViolationsGroupViolation[];
}

interface IViolationsErrorIconProps {
  sectionId: MenuItemsIdsEnum;
  violations: IKnmDataViolationExtended[] | undefined;
  changeResultsOpenedFlags: (id: string, value: boolean) => void;
}

export const ViolationsErrorIcon = ({ sectionId, violations, changeResultsOpenedFlags }: IViolationsErrorIconProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    function handleDocumentClickOutside(event: MouseEvent) {
      if (containerRef.current) {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setIsActive(false);
        }
      }
    }

    document.addEventListener('mousedown', handleDocumentClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClickOutside);
    };
  }, [containerRef]);

  const handleIconClick = useCallback(() => setIsActive(prev => !prev), []);

  const makeViolationLinkClickHandler = (
    violationPathTemplate: IViolationsGroupViolation['pathTemplate'],
    reserveElementId: IViolationsGroupViolation['reserveScrollElementId'],
  ) => (e: ReactMouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    let scrollElementId = e.currentTarget.hash.replace('#', '');
    let scrollElement = scrollElementId && document.getElementById(scrollElementId);

    if (scrollElement) {
      if (
        violationPathTemplate === KnmDataViolationPathsTemplateEnum.RESULT ||
        violationPathTemplate === KnmDataViolationPathsTemplateEnum.RESULT_ACT_CREATE_DATE ||
        violationPathTemplate === KnmDataViolationPathsTemplateEnum.RESULT_ADDITIONAL_INFO ||
        violationPathTemplate === KnmDataViolationPathsTemplateEnum.RESULT_DELEGATES ||
        violationPathTemplate === KnmDataViolationPathsTemplateEnum.RESULT_DURATION_DAYS ||
        violationPathTemplate === KnmDataViolationPathsTemplateEnum.RESULT_DURATION_HOURS ||
        violationPathTemplate === KnmDataViolationPathsTemplateEnum.RESULT_INSPECTORS ||
        violationPathTemplate === KnmDataViolationPathsTemplateEnum.RESULT_START_DATE
      ) {
        changeResultsOpenedFlags(scrollElementId, true);
      }
    } else if (!scrollElement && reserveElementId) {
      scrollElementId = reserveElementId;
      scrollElement = document.getElementById(reserveElementId);
    }
    if (scrollElement) {
      window.scrollTo({
        top: scrollElement.getBoundingClientRect().top + window.pageYOffset - HEADER_STICKED_HEIGHT - 5,
        behavior: 'smooth',
      });
    }
  };

  const violationsGrouped: IViolationsGroup[] = useMemo(() => {
    const groups: IViolationsGroup[] = [];

    violations?.forEach(violation => {
      const violationGroupType = violation.value
        ? IViolationsGroupTypesEnum.DATA_FILLED
        : IViolationsGroupTypesEnum.DATA_EMPTY;
      const violationGroup = groups.find(
        group => group.count === violation.countDateViolation && group.groupType === violationGroupType,
      );
      let { scrollElementId } = menuViolationsDataByPathMap[violation.pathTemplate];
      let reserveScrollElementId = '';

      if (
        violation.pathTemplate === KnmDataViolationPathsTemplateEnum.OBJECTS ||
        violation.pathTemplate === KnmDataViolationPathsTemplateEnum.SUPERVISIONS ||
        violation.pathTemplate === KnmDataViolationPathsTemplateEnum.RESULT ||
        violation.pathTemplate === KnmDataViolationPathsTemplateEnum.RESULT_ACT_CREATE_DATE ||
        violation.pathTemplate === KnmDataViolationPathsTemplateEnum.RESULT_ADDITIONAL_INFO ||
        violation.pathTemplate === KnmDataViolationPathsTemplateEnum.RESULT_DELEGATES ||
        violation.pathTemplate === KnmDataViolationPathsTemplateEnum.RESULT_DURATION_DAYS ||
        violation.pathTemplate === KnmDataViolationPathsTemplateEnum.RESULT_DURATION_HOURS ||
        violation.pathTemplate === KnmDataViolationPathsTemplateEnum.RESULT_INSPECTORS ||
        violation.pathTemplate === KnmDataViolationPathsTemplateEnum.RESULT_START_DATE
      ) {
        reserveScrollElementId = scrollElementId;
        const objectId = violation.path.split('/')[3];

        if (
          violation.pathTemplate === KnmDataViolationPathsTemplateEnum.OBJECTS ||
          violation.pathTemplate === KnmDataViolationPathsTemplateEnum.SUPERVISIONS
        ) {
          scrollElementId = getObjectBlockId(objectId);
        } else if (
          violation.pathTemplate === KnmDataViolationPathsTemplateEnum.RESULT ||
          violation.pathTemplate === KnmDataViolationPathsTemplateEnum.RESULT_ACT_CREATE_DATE ||
          violation.pathTemplate === KnmDataViolationPathsTemplateEnum.RESULT_ADDITIONAL_INFO ||
          violation.pathTemplate === KnmDataViolationPathsTemplateEnum.RESULT_DELEGATES ||
          violation.pathTemplate === KnmDataViolationPathsTemplateEnum.RESULT_DURATION_DAYS ||
          violation.pathTemplate === KnmDataViolationPathsTemplateEnum.RESULT_DURATION_HOURS ||
          violation.pathTemplate === KnmDataViolationPathsTemplateEnum.RESULT_INSPECTORS ||
          violation.pathTemplate === KnmDataViolationPathsTemplateEnum.RESULT_START_DATE
        ) {
          scrollElementId = getObjectResultBlockId(objectId);
        }
      }
      const countDateViolation = {
        ...violation,
        groupType: violationGroupType,
        title: menuViolationsDataByPathMap[violation.pathTemplate].title,
        scrollElementId,
        reserveScrollElementId,
      };

      if (!violationGroup) {
        groups.push({
          count: violation.countDateViolation,
          groupType: violationGroupType,
          violations: [countDateViolation],
        });
      } else {
        violationGroup.violations.push(countDateViolation);
      }
    });
    groups.forEach(countDate =>
      countDate.violations.sort(
        (a, b) =>
          knmSectionsViolationsPathsTemplatesMap[sectionId].indexOf(a.pathTemplate) -
          knmSectionsViolationsPathsTemplatesMap[sectionId].indexOf(b.pathTemplate),
      ),
    );

    return groups;
  }, [sectionId, violations]);

  if (!violationsGrouped?.length) {
    return null;
  }

  const containerAttributes = {
    ...(isActive && { 'data-active': true }),
  };

  const tooltipAttributes = {
    ...(isActive && { 'data-active': true }),
  };

  return (
    <div className={styles.Container} ref={containerRef} {...containerAttributes}>
      <span className={styles.Icon} onClick={handleIconClick} />
      <div className={styles.Tooltip} {...tooltipAttributes}>
        <ul className={styles.CountDatesList}>
          {violationsGrouped.map((violationsGroup, index) => (
            <li key={`${violationsGroup.count}-${index}`}>
              <span className={styles.CountDateTitle}>
                {violationsGroup.groupType === IViolationsGroupTypesEnum.DATA_FILLED
                  ? `Данные сведения внесены с опозданием на ${violationsGroup.count} 
                  ${declOfNum(violationsGroup.count, countTitles)}:`
                  : `Данные сведения отсутствуют. Срок нарушения ${violationsGroup.count} ${declOfNum(
                      violationsGroup.count,
                      countTitles,
                    )}:`}
              </span>
              <ul className={styles.ViolationsList}>
                {violationsGroup.violations.map(violation => (
                  <li key={violation.path}>
                    <a
                      className={styles.ViolationLink}
                      href={`#${violation.scrollElementId}`}
                      onClick={makeViolationLinkClickHandler(violation.pathTemplate, violation.reserveScrollElementId)}
                    >
                      {violation.title}
                    </a>
                    {renderViolationValue(violation)}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
